import { useEffect, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { isAuthenticated, user } from '@stores/authStore';
import { api } from '@services/api';

type TGStatus = 'pending' | 'approved' | 'rejected';

interface TelegramUserRow {
  id: number;
  chat_id: number;
  user: { id: number; email: string; name: string };
  telegram_username: string | null;
  telegram_first_name: string | null;
  status: TGStatus;
  is_admin: boolean;
  project_ids: number[];
  created_at: string;
  approved_at: string | null;
}

interface ProjectItem {
  id: number;
  name: string;
  code: string;
}

export function TelegramUsersPage() {
  const [rows, setRows] = useState<TelegramUserRow[]>([]);
  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [filter, setFilter] = useState<TGStatus | ''>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated.value) {
      route('/login');
      return;
    }
    if (!user.value?.is_admin) {
      route('/dashboard');
      return;
    }
    Promise.all([loadProjects(), loadRows()]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadRows();
  }, [filter]);

  const loadProjects = async () => {
    const resp = await api.get<any>('/api/projects');
    if (resp.success && resp.data) {
      const items = resp.data.items || resp.data;
      setProjects(items as ProjectItem[]);
    }
  };

  const loadRows = async () => {
    const qs = filter ? `?status=${filter}` : '';
    const resp = await api.get<TelegramUserRow[]>(`/api/telegram-users${qs}`);
    if (resp.success && resp.data) {
      setRows(resp.data);
    }
  };

  const setStatus = async (row: TelegramUserRow, status: TGStatus) => {
    const resp = await api.patch<TelegramUserRow>(
      `/api/telegram-users/${row.id}`,
      { status },
    );
    if (resp.success && resp.data) {
      setRows((rs) => rs.map((r) => (r.id === row.id ? resp.data! : r)));
    }
  };

  const toggleProject = async (row: TelegramUserRow, pid: number) => {
    const next = row.project_ids.includes(pid)
      ? row.project_ids.filter((x) => x !== pid)
      : [...row.project_ids, pid];
    const resp = await api.put<TelegramUserRow>(
      `/api/telegram-users/${row.id}/projects`,
      { project_ids: next },
    );
    if (resp.success && resp.data) {
      setRows((rs) => rs.map((r) => (r.id === row.id ? resp.data! : r)));
    }
  };

  if (loading) {
    return (
      <div class="p-8 text-gray-600">로딩 중...</div>
    );
  }

  return (
    <div class="p-6 max-w-6xl mx-auto">
      <div class="flex items-center justify-between mb-6">
        <h1 class="text-2xl font-bold">텔레그램 사용자 관리</h1>
        <button
          onClick={() => route('/dashboard')}
          class="text-sm text-gray-600 hover:text-gray-900"
        >
          ← 대시보드
        </button>
      </div>

      <div class="mb-4 flex items-center gap-3">
        <label class="text-sm text-gray-700">상태 필터:</label>
        <select
          value={filter}
          onChange={(e) => setFilter((e.target as HTMLSelectElement).value as TGStatus | '')}
          class="border rounded px-2 py-1 text-sm"
        >
          <option value="">전체</option>
          <option value="pending">대기</option>
          <option value="approved">승인</option>
          <option value="rejected">거절</option>
        </select>
        <span class="ml-auto text-sm text-gray-500">{rows.length}건</span>
      </div>

      {rows.length === 0 ? (
        <div class="text-center text-gray-500 py-12 bg-gray-50 rounded">
          해당 상태의 텔레그램 사용자가 없습니다.
        </div>
      ) : (
        <div class="overflow-x-auto bg-white rounded shadow">
          <table class="w-full text-sm">
            <thead class="bg-gray-100 text-left">
              <tr>
                <th class="px-4 py-2">계정</th>
                <th class="px-4 py-2">텔레그램</th>
                <th class="px-4 py-2">상태</th>
                <th class="px-4 py-2">프로젝트</th>
                <th class="px-4 py-2">액션</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} class="border-t align-top">
                  <td class="px-4 py-3">
                    <div class="font-medium">{row.user.name}</div>
                    <div class="text-xs text-gray-500">{row.user.email}</div>
                  </td>
                  <td class="px-4 py-3">
                    <div>@{row.telegram_username ?? '-'}</div>
                    <div class="text-xs text-gray-500">
                      {row.telegram_first_name ?? '-'}
                      {row.is_admin && <span class="ml-1 px-1 rounded bg-indigo-100 text-indigo-700">관리자</span>}
                    </div>
                  </td>
                  <td class="px-4 py-3">
                    <StatusBadge status={row.status} />
                  </td>
                  <td class="px-4 py-3">
                    <div class="flex flex-wrap gap-2">
                      {projects.map((p) => (
                        <label key={p.id} class="inline-flex items-center gap-1 text-xs">
                          <input
                            type="checkbox"
                            checked={row.project_ids.includes(p.id)}
                            onChange={() => toggleProject(row, p.id)}
                          />
                          <span>{p.name}</span>
                        </label>
                      ))}
                    </div>
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap">
                    {row.status !== 'approved' && (
                      <button
                        onClick={() => setStatus(row, 'approved')}
                        class="mr-2 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        승인
                      </button>
                    )}
                    {row.status !== 'rejected' && (
                      <button
                        onClick={() => setStatus(row, 'rejected')}
                        class="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        거절
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: TGStatus }) {
  const map: Record<TGStatus, { text: string; cls: string }> = {
    pending: { text: '대기', cls: 'bg-yellow-100 text-yellow-800' },
    approved: { text: '승인', cls: 'bg-green-100 text-green-800' },
    rejected: { text: '거절', cls: 'bg-red-100 text-red-800' },
  };
  const m = map[status];
  return <span class={`px-2 py-0.5 rounded text-xs ${m.cls}`}>{m.text}</span>;
}
